import React, { useState, useEffect } from 'react';
import WorksheetTabs from './WorksheetTabs';
import AdminSpreadsheet from './AdminSpreadsheet';
import ChangePassword from './ChangePassword';

// Use the PHP backend URL directly
const API_BASE_URL = 'http://spreadsheets.isilverstar.in/api';

const AdminPanel = ({ onLogout }) => {
  const [worksheets, setWorksheets] = useState([]);
  const [activeWorksheetId, setActiveWorksheetId] = useState(null);
  const [activeWorksheet, setActiveWorksheet] = useState(null);
  const [cells, setCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [newWorksheetName, setNewWorksheetName] = useState('');

  // Fetch all worksheets
  useEffect(() => {
    const fetchWorksheets = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/worksheets`);
        if (!response.ok) {
          throw new Error('Failed to fetch worksheets');
        }
        const data = await response.json();
        setWorksheets(data);
        
        // Set the first worksheet as active if none is selected
        if (data.length > 0 && !activeWorksheetId) {
          setActiveWorksheetId(data[0].id);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWorksheets();
  }, []);

  // Fetch worksheet data when active worksheet changes
  useEffect(() => {
    if (!activeWorksheetId) return;

    const fetchWorksheetData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/worksheets/${activeWorksheetId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch worksheet data');
        }
        const data = await response.json();
        setActiveWorksheet(data.worksheet);
        setCells(data.cells);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWorksheetData();
  }, [activeWorksheetId]);

  const handleSelectWorksheet = (worksheetId) => {
    setActiveWorksheetId(worksheetId);
  };

  const handleCreateWorksheet = async () => {
    if (!newWorksheetName.trim()) return;

    try {
      const response = await fetch(`${API_BASE_URL}/worksheets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newWorksheetName }),
      });

      if (!response.ok) {
        throw new Error('Failed to create worksheet');
      }

      const newWorksheet = await response.json();
      
      // Add the new worksheet to the list
      setWorksheets([...worksheets, { id: newWorksheet.id, name: newWorksheet.name }]);
      setNewWorksheetName('');
      
      // Set the new worksheet as active
      setActiveWorksheetId(newWorksheet.id);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRenameWorksheet = async () => {
    if (!activeWorksheet || !activeWorksheet.name.trim()) return;

    try {
      const response = await fetch(`${API_BASE_URL}/worksheets/${activeWorksheetId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: activeWorksheet.name }),
      });

      if (!response.ok) {
        throw new Error('Failed to rename worksheet');
      }

      // Update the worksheet in the list
      setWorksheets(worksheets.map(ws => 
        ws.id === activeWorksheetId ? { ...ws, name: activeWorksheet.name } : ws
      ));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteWorksheet = async () => {
    if (!activeWorksheetId) return;

    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete the worksheet "${activeWorksheet?.name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/worksheets/${activeWorksheetId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete worksheet');
      }

      // Remove the worksheet from the list
      const updatedWorksheets = worksheets.filter(ws => ws.id !== activeWorksheetId);
      setWorksheets(updatedWorksheets);
      
      // If there are other worksheets, select the first one
      if (updatedWorksheets.length > 0) {
        setActiveWorksheetId(updatedWorksheets[0].id);
      } else {
        setActiveWorksheetId(null);
        setActiveWorksheet(null);
        setCells([]);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSaveWorksheet = async (updatedCells) => {
    if (!activeWorksheetId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/worksheets/${activeWorksheetId}/data`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ cells: updatedCells }),
      });

      if (!response.ok) {
        throw new Error('Failed to save worksheet data');
      }

      // Update the cells in state
      setCells(updatedCells);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleWorksheetNameChange = (name) => {
    setActiveWorksheet({ ...activeWorksheet, name });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="admin-panel p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Admin Panel</h1>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowChangePassword(true)}
            className="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded"
          >
            chnge password
          </button>
          <button 
            onClick={onLogout}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
          >
            Logout
          </button>
        </div>
      </div>
      
      <div className="mb-6 flex space-x-2">
        <input
          type="text"
          value={newWorksheetName}
          onChange={(e) => setNewWorksheetName(e.target.value)}
          placeholder="New worksheet name"
          className="border border-gray-300 rounded px-3 py-2"
        />
        <button
          onClick={handleCreateWorksheet}
          className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
        >
          Create Worksheet
        </button>
      </div>
      
      {worksheets.length > 0 ? (
        <>
          <WorksheetTabs 
            worksheets={worksheets} 
            activeWorksheetId={activeWorksheetId} 
            onSelectWorksheet={handleSelectWorksheet} 
          />
          
          {activeWorksheet && (
            <div className="mb-6">
              <div className="flex space-x-2 mb-4">
                <input
                  type="text"
                  value={activeWorksheet.name}
                  onChange={(e) => handleWorksheetNameChange(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-2"
                />
                <button
                  onClick={handleRenameWorksheet}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  Rename
                </button>
                <button
                  onClick={handleDeleteWorksheet}
                  className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
                >
                  Delete
                </button>
              </div>
              
              <AdminSpreadsheet 
                worksheet={activeWorksheet} 
                cells={cells} 
                onSave={handleSaveWorksheet}
              />
            </div>
          )}
        </>
      ) : (
        <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">No worksheets found. </strong>
          <span className="block sm:inline">Create a new worksheet to get started.</span>
        </div>
      )}
      {showChangePassword && (
        <ChangePassword onClose={() => setShowChangePassword(false)} onSuccess={() => setShowChangePassword(false)} />
      )}
    </div>
  );
};

export default AdminPanel;